import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import Highlight from '../../components/base/Highlight';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData } from '../../utils/structuredData';

export default function Campaign() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXキャンペーン情報まとめ | 最大500,000円キャッシュバック',
    description: 'DMM FXの最新キャンペーン情報を完全網羅。新規口座開設で最大500,000円キャッシュバック、取引応援ポイントサービス、スマホで簡単本人確認など、お得なキャンペーンの詳細と受け取り方を解説します。',
    keywords: 'DMM FX,キャンペーン,キャッシュバック,ポイント,新規口座開設,特典,お得',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXキャンペーン情報まとめ',
        description: 'DMM FXの最新キャンペーン情報。最大500,000円キャッシュバックなど、お得なキャンペーンの詳細を解説します。',
        author: 'DMM FX情報局',
        datePublished: '2024-12-01',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/20e7e4544e068a9d47e2def311e03e5d.jpg',
        url: '/campaign',
      }),
      createFAQStructuredData([
        {
          question: 'キャッシュバックを受け取るための条件は何ですか？',
          answer: '新規口座開設完了後、口座開設完了日から3ヶ月以内に新規取引を行い、取引数量に応じてキャッシュバックが受けられます。最低1Lot以上の取引で2,000円から、最大5,000Lot以上で500,000円のキャッシュバックとなります。',
        },
        {
          question: 'キャッシュバックはいつ受け取れますか？',
          answer: 'キャンペーン条件達成の翌月中旬に、取引口座へ自動入金されます。別途申請手続きは不要です。',
        },
        {
          question: '取引応援ポイントサービスとは何ですか？',
          answer: '新規取引1Lotごとに1ポイントが貯まるサービスです。貯まったポイントは1ポイント=1円で現金に交換可能で、ランクに応じて最大3倍のポイントが獲得できます。有効期限もありません。',
        },
        {
          question: 'キャンペーンは法人口座でも適用されますか？',
          answer: 'いいえ、新規口座開設キャッシュバックキャンペーンは個人口座のみが対象となります。法人口座は対象外です。',
        },
        {
          question: '複数のキャンペーンを同時に利用できますか？',
          answer: 'はい、新規口座開設キャッシュバックと取引応援ポイントサービスは併用可能です。お得に取引を始められます。',
        },
      ]),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'キャンペーン情報', url: '/campaign' },
      ]),
    ],
  });

  const mainCampaign = {
    title: '新規口座開設キャンペーン',
    cashback: '最大500,000円',
    period: '2026年実施中',
    conditions: [
      '新規口座開設完了',
      '口座開設完了日から3ヶ月以内に新規取引',
      '取引数量に応じてキャッシュバック',
    ],
    details: [
      { lots: '5,000Lot以上', cashback: '500,000円' },
      { lots: '4,000Lot以上', cashback: '400,000円' },
      { lots: '3,000Lot以上', cashback: '300,000円' },
      { lots: '2,500Lot以上', cashback: '250,000円' },
      { lots: '2,000Lot以上', cashback: '200,000円' },
      { lots: '1,500Lot以上', cashback: '150,000円' },
      { lots: '1,000Lot以上', cashback: '100,000円' },
      { lots: '500Lot以上', cashback: '50,000円' },
      { lots: '250Lot以上', cashback: '25,000円' },
      { lots: '100Lot以上', cashback: '10,000円' },
      { lots: '40Lot以上', cashback: '4,000円' },
      { lots: '1Lot以上', cashback: '2,000円' },
    ],
  };

  const otherCampaigns = [
    {
      title: '取引応援ポイントサービス',
      description: '取引するたびにポイントが貯まる！貯まったポイントは現金に交換可能',
      icon: 'ri-gift-line',
      color: 'from-purple-500 to-pink-600',
      features: [
        '新規取引1Lotごとに1ポイント',
        '1ポイント=1円で現金交換可能',
        'ランクに応じてポイント最大3倍',
        '有効期限なしで安心',
      ],
      ongoing: true,
    },
    {
      title: '新通貨ペア取扱開始',
      description: 'TRY/JPY（トルコリラ/円）、AUD/NZD（豪ドル/NZドル）の取引が可能に',
      icon: 'ri-global-line',
      color: 'from-blue-500 to-cyan-600',
      features: [
        '高金利通貨ペアTRY/JPY追加',
        'オセアニア通貨ペアAUD/NZD追加',
        '取引の選択肢がさらに広がる',
        '業界最狭水準のスプレッド',
      ],
      ongoing: true,
    },
    {
      title: 'スマホで簡単本人確認',
      description: 'マイナンバーカードをかざすだけで本人確認が完了',
      icon: 'ri-smartphone-line',
      color: 'from-emerald-500 to-teal-600',
      features: [
        'マイナンバーカードで簡単認証',
        '最短即日で取引開始可能',
        'スマホアプリで完結',
        '郵送不要でスピーディー',
      ],
      ongoing: true,
    },
  ];

  const howToReceive = [
    {
      step: 1,
      title: '口座開設',
      description: 'DMM FXの公式サイトから口座開設を申し込みます',
      icon: 'ri-user-add-line',
    },
    {
      step: 2,
      title: '取引開始',
      description: '口座開設完了後、3ヶ月以内に新規取引を開始',
      icon: 'ri-exchange-line',
    },
    {
      step: 3,
      title: '条件達成',
      description: 'キャンペーン条件の取引数量を達成',
      icon: 'ri-trophy-line',
    },
    {
      step: 4,
      title: 'キャッシュバック',
      description: '条件達成の翌月中旬に自動入金',
      icon: 'ri-money-dollar-circle-line',
    },
  ];

  const notes = [
    'キャッシュバックは取引口座に自動入金されます',
    '新規取引のみが対象です（決済取引は対象外）',
    'キャンペーン期間中に口座開設した方が対象です',
    '法人口座は対象外となります',
    'キャッシュバック金額は取引数量に応じて変動します',
    '詳細な条件は公式サイトをご確認ください',
  ];

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'DMM FX取引ガイド', url: '/fx-trade' },
            { name: 'キャンペーン情報', url: '/campaign' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="pt-20 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-gradient-to-r from-amber-500 to-orange-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-bold mb-4 md:mb-6">
            <i className="ri-fire-line mr-1"></i>
            期間限定キャンペーン実施中
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            DMM FX<br className="md:hidden" />キャンペーン情報まとめ
          </h1>
          <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8">
            <Highlight variant="badge" color="red">最大500,000円</Highlight>キャッシュバック！お得に取引を始めよう
          </p>
        </div>
      </section>

      {/* Main Campaign Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 text-white mb-8 md:mb-12">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">{mainCampaign.title}</h2>
              <div className="text-4xl md:text-6xl lg:text-7xl font-bold mb-2">
                <Highlight variant="bold" color="red">{mainCampaign.cashback}</Highlight>
              </div>
              <p className="text-base md:text-lg text-white/90">キャッシュバック</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 mb-6 md:mb-8">
              <h3 className="text-base md:text-xl font-bold mb-3 md:mb-4 flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                キャンペーン期間
              </h3>
              <p className="text-sm md:text-lg">{mainCampaign.period}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 mb-6 md:mb-8">
              <h3 className="text-base md:text-xl font-bold mb-3 md:mb-4 flex items-center">
                <i className="ri-checkbox-circle-line mr-2"></i>
                参加条件
              </h3>
              <ul className="space-y-2">
                {mainCampaign.conditions.map((condition, index) => (
                  <li key={index} className="flex items-center text-sm md:text-base">
                    <i className="ri-check-line mr-2 text-base md:text-xl"></i>
                    {condition}
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-center">
              <a 
                href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
                rel="nofollow"
                className="inline-block bg-white text-teal-600 px-6 md:px-8 py-3 md:py-4 rounded-lg text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
              >
                <Highlight variant="bold" color="red">今すぐ口座開設</Highlight>してキャンペーンに参加
              </a>
              <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
            </div>
          </div>

          {/* Cashback Details */}
          <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 md:px-6 py-3 md:py-4">
              <h3 className="text-base md:text-xl font-bold text-gray-900">キャッシュバック金額詳細</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm md:text-base">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold text-gray-900">取引数量</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-right text-xs md:text-sm font-bold text-teal-600">キャッシュバック金額</th>
                  </tr>
                </thead>
                <tbody>
                  {mainCampaign.details.slice(0, 6).map((detail, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm font-medium text-gray-900">{detail.lots}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-right text-sm md:text-lg font-bold">
                        <Highlight variant="bold" color="red">{detail.cashback}</Highlight>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Other Campaigns Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">
            その他のキャンペーン
          </h2>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {otherCampaigns.map((campaign, index) => (
              <div key={index} className="bg-white rounded-xl md:rounded-2xl p-5 md:p-8 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br ${campaign.color} rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6`}>
                  <i className={`${campaign.icon} text-white text-xl md:text-3xl`}></i>
                </div>
                
                {campaign.ongoing && (
                  <div className="inline-block bg-emerald-100 text-emerald-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs font-bold mb-3 md:mb-4">
                    常時開催中
                  </div>
                )}
                
                <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">{campaign.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-4 md:mb-6">{campaign.description}</p>
                
                <ul className="space-y-1 md:space-y-2">
                  {campaign.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-xs md:text-sm text-gray-700">
                      <i className="ri-check-line text-teal-500 mr-2 flex-shrink-0 mt-0.5"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mt-8 md:mt-12 bg-white rounded-xl p-4 md:p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* How to Receive Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">
            キャッシュバックの受け取り方
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {howToReceive.map((item, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-4 md:mb-6">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <i className={`${item.icon} text-white text-xl md:text-3xl`}></i>
                  </div>
                  <div className="absolute -top-1 md:-top-2 -right-1 md:-right-2 w-7 h-7 md:w-10 md:h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-xs md:text-base">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">{item.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 hidden sm:block">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 md:mt-12">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* Notes Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-8 border border-amber-200">
            <h3 className="text-base md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
              <i className="ri-information-line text-amber-600 mr-2 text-xl md:text-2xl"></i>
              注意事項
            </h3>
            <ul className="space-y-2 md:space-y-3">
              {notes.map((note, index) => (
                <li key={index} className="flex items-start text-xs md:text-sm text-gray-700">
                  <i className="ri-arrow-right-s-line text-amber-600 mr-2 flex-shrink-0 mt-0.5"></i>
                  {note}
                </li>
              ))}
            </ul>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mt-6 md:mt-8 bg-white rounded-xl p-4 md:p-6 border-l-4 border-emerald-500 shadow-md">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-4">関連記事</h2>
            <p className="text-gray-600 text-sm md:text-base">もっと詳しく知りたい方へ</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <Link to="/account-opening" className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-file-list-3-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">口座開設の手順</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4">画像付きで分かりやすく解説</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/beginner-guide" className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-book-open-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">初心者でも大丈夫？</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4">FX初心者向けの完全ガイド</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/deposit-withdrawal" className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-bank-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">入出金方法と手数料</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4">クイック入金の使い方を解説</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
            今がチャンス！お得に取引を始めよう
          </h2>
          <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8">
            <Highlight variant="badge" color="red">最大500,000円</Highlight>のキャッシュバックを受け取って、FX取引をスタート
          </p>
          <CTAButton />
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}